import { ajax } from "discourse/lib/ajax";
import DiscourseRoute from "discourse/routes/discourse";

export default class ProxyviewsGuideRoute extends DiscourseRoute {
  _scrollHandler = null;
  _clickHandler = null;

  async model(params) {
    const data = await ajax(`/guides/${params.slug}.json`);

    let modifiedContent = data.content || "";
    const toc = [];

    if (typeof window !== "undefined" && window.DOMParser && data.content) {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data.content, "text/html");
        const headings = doc.querySelectorAll("h2, h3");

        headings.forEach((heading, idx) => {
          let id = heading.id;
          if (!id) {
            id = heading.textContent
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/(^-|-$)/g, "");
            if (!id) id = `heading-${idx}`;

            if (doc.getElementById(id)) {
              id = `${id}-${idx}`;
            }
            heading.setAttribute("id", id);
          }

          toc.push({
            id,
            text: heading.textContent.trim(),
            level: heading.tagName.toLowerCase(),
          });
        });

        modifiedContent = doc.body.innerHTML;
      } catch (e) {
        console.error("Error parsing TOC headers:", e);
      }
    }

    return {
      ...data,
      content: modifiedContent,
      toc,
      categoryLabel: data.category
        ? data.category.replace(/-/g, " ")
        : null,
      categoryUpper: data.category
        ? data.category.replace(/-/g, " ").toUpperCase()
        : null,
      titleUpper: data.title ? data.title.toUpperCase() : "",
      freshnessGradeUpper: data.freshness_grade
        ? data.freshness_grade.toUpperCase()
        : null,
      publishedLabel: data.published_at
        ? new Date(data.published_at).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : null,
      relatedComparisons: data.related_comparisons || [],
    };
  }

  titleToken() {
    return this.currentModel?.title || "Guide";
  }

  activate() {
    super.activate();
    this._setupScrollFeatures();
    this._clickHandler = this._handleGlobalClick.bind(this);
    document.addEventListener("click", this._clickHandler);
  }

  deactivate() {
    super.deactivate();
    this._teardownScrollFeatures();
    if (this._clickHandler) {
      document.removeEventListener("click", this._clickHandler);
      this._clickHandler = null;
    }
  }

  _setupScrollFeatures() {
    if (typeof window === "undefined") return;

    const progressBar = document.getElementById("reading-progress-bar");
    const backToTopBtn = document.getElementById("back-to-top-btn");
    const guideEl = document.querySelector(".pd-guide");
    const tocLinks = document.querySelectorAll(".pd-toc-sidebar__link");

    this._scrollHandler = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;

      // Reading progress
      if (progressBar && docHeight > 0) {
        const progress = Math.min((scrollTop / docHeight) * 100, 100);
        progressBar.style.width = `${progress}%`;
      }

      // Back to top visibility
      if (backToTopBtn) {
        if (scrollTop > 400) {
          backToTopBtn.classList.add("pd-back-to-top--visible");
        } else {
          backToTopBtn.classList.remove("pd-back-to-top--visible");
        }
      }

      // TOC active state
      if (tocLinks.length && guideEl) {
        const headings = guideEl.querySelectorAll("h2[id], h3[id]");
        let activeId = null;

        headings.forEach((heading) => {
          const rect = heading.getBoundingClientRect();
          if (rect.top <= 120) {
            activeId = heading.id;
          }
        });

        tocLinks.forEach((link) => {
          const linkId = link.getAttribute("data-toc-id");
          if (linkId === activeId) {
            link.classList.add("pd-toc-sidebar__link--active");
          } else {
            link.classList.remove("pd-toc-sidebar__link--active");
          }
        });
      }
    };

    window.addEventListener("scroll", this._scrollHandler, {
      passive: true,
    });
    this._scrollHandler();
  }

  _teardownScrollFeatures() {
    if (this._scrollHandler) {
      window.removeEventListener("scroll", this._scrollHandler);
      this._scrollHandler = null;
    }

    if (typeof document !== "undefined") {
      const progressBar = document.getElementById("reading-progress-bar");
      if (progressBar) progressBar.style.width = "0%";

      const backToTopBtn = document.getElementById("back-to-top-btn");
      if (backToTopBtn)
        backToTopBtn.classList.remove("pd-back-to-top--visible");
    }
  }

  _handleGlobalClick(e) {
    // FAQ Accordion Toggle
    const faqBtn = e.target.closest(".pd-faq__question");
    if (faqBtn) {
      const item = faqBtn.closest(".pd-faq__item");
      if (!item) return;
      const isExpanded = faqBtn.getAttribute("aria-expanded") === "true";

      faqBtn.setAttribute("aria-expanded", String(!isExpanded));
      if (!isExpanded) {
        item.classList.add("pd-faq__item--expanded");
      } else {
        item.classList.remove("pd-faq__item--expanded");
      }
      return;
    }

    // Back to top
    const backToTop = e.target.closest("#back-to-top-btn");
    if (backToTop) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Helpfulness feedback
    const feedbackBtn = e.target.closest(".pd-helpful-widget__btn");
    if (feedbackBtn) {
      const helpful = feedbackBtn.id === "feedback-yes-btn";
      const slug = this.currentModel?.slug;
      if (!slug) return;

      const widget = feedbackBtn.closest(".pd-helpful-widget");
      const actions = widget.querySelector(".pd-helpful-widget__actions");
      const thanksMsg = widget.querySelector(".pd-helpful-widget__thanks");
      const errorMsg = widget.querySelector(".pd-helpful-widget__error");

      fetch(`/guides/${slug}/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute("content") || "",
        },
        body: JSON.stringify({ helpful }),
      })
        .then((res) => {
          if (!res.ok) throw new Error("Feedback request failed");
          return res.json();
        })
        .then(() => {
          actions.style.display = "none";
          thanksMsg.style.display = "inline-block";
          if (errorMsg) errorMsg.style.display = "none";
        })
        .catch(() => {
          actions.style.display = "none";
          if (errorMsg) {
            errorMsg.style.display = "inline-block";
          } else {
            thanksMsg.style.display = "inline-block";
          }
        });
    }
  }
}
